import { useMemo, useRef, useEffect, useState } from 'react';
import { FormControl, ButtonGroup, Button, IconButton, Tooltip } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import WindDirectionCell from './table/WindDirectionCell';
import { 
  type MRT_ColumnDef, 
  type MRT_Cell,
  type MRT_TableInstance
} from 'material-react-table';
import { SkiAreaSensorTable } from './SkiAreaSensorTable';
import DraggableContainer from './DraggableContainer';
import DayHeader from './table/DayHeader';
import BaseHeader from './table/BaseHeader';
import DataCell from './table/DataCell';
import SnowRateCell from './table/SnowRateCell';
import {
  timeColumnHeadStyle,
  timeColumnBodyStyle,
  stationNameHeadStyle,
  stationNameBodyStyle
} from '../assets/styles/StationDataTable.styles';
import { getSnowLevelStyle } from '../utils/colorCalculators';
import { findCurrentPeriodTimestamp } from '../utils/timeUtils';

type GroupInterval = '1' | '3' | '6' | '12' | '24';

// Utility function to group timestamps and values by interval
const groupByInterval = (timestamps: string[], values: Array<number | null>, intervalHours: number, aggregationFn?: (values: number[]) => number): { timestamps: string[], values: Array<number | null> } => {
  if (!timestamps.length) return { timestamps: [], values: [] };
  
  const groups = new Map<string, number[]>();
  const groupTimestamps = new Map<string, string>();
  
  timestamps.forEach((timestamp, index) => {
    const value = values[index];
    if (value === null) return;
    
    const date = new Date(timestamp);
    const hours = date.getHours();
    const roundedHours = Math.floor(hours / intervalHours) * intervalHours;
    
    // Find the first timestamp in this interval to use as the key
    const key = timestamps.find(t => {
      const tDate = new Date(t);
      return tDate.getHours() === roundedHours && 
             tDate.getDate() === date.getDate() &&
             tDate.getMonth() === date.getMonth() &&
             tDate.getFullYear() === date.getFullYear();
    }) || timestamp;
    
    if (!groups.has(key)) {
      groups.set(key, []);
      groupTimestamps.set(key, timestamp);
    }
    groups.get(key)?.push(value);
  });

  // Sort and convert back to arrays
  const sortedEntries = Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  const newTimestamps = sortedEntries.map(([key]) => groupTimestamps.get(key) || key);
  const newValues = sortedEntries.map(([_, vals]) => {
    if (!vals.length) return null;
    return aggregationFn ? aggregationFn(vals) : vals.reduce((sum, val) => sum + val, 0) / vals.length;
  });

  return { timestamps: newTimestamps, values: newValues };
};

// Aggregation functions
const aggregateFunctions = {
  sum: (values: number[]) => values.reduce((a, b) => a + b, 0),
  mean: (values: number[]) => values.reduce((a, b) => a + b, 0) / values.length,
  max: (values: number[]) => Math.max(...values)
};

// Vector average for wind direction in degrees
const vectorAverageWindDirection = (degrees: number[]) => {
  // Convert degrees to radians and calculate average of sin and cos components
  const sumSin = degrees.reduce((sum, deg) => sum + Math.sin(deg * Math.PI / 180), 0);
  const sumCos = degrees.reduce((sum, deg) => sum + Math.cos(deg * Math.PI / 180), 0);
  
  // Convert back to degrees
  const avgDegrees = Math.atan2(sumSin / degrees.length, sumCos / degrees.length) * 180 / Math.PI;
  
  // Normalize to 0-360 range
  return (avgDegrees + 360) % 360;
};

const getAggregationFunction = (key: string) => {
  const keyLower = key.toLowerCase();
  if (keyLower.includes('snow_hourly') || keyLower.includes('preciprate') || keyLower === 'swe') {
    return aggregateFunctions.sum;
  }
  if (keyLower.includes('snow_level')) {
    return aggregateFunctions.mean;
  }
  if (keyLower.includes('temperature')) {
    return aggregateFunctions.mean;
  }
  if (keyLower.includes('wind_direction')) {
    return vectorAverageWindDirection;
  }
  return (values: number[]) => values[values.length - 1];
};

interface SkiAreaMetadata {
  base_elevation: number;
  top_elevation: number;
  latitude: number;
  longitude: number;
  name: string;
  timezone: string;
  summit_wind_sensor_elevation: number;
}

interface SkiAreaDataStructure {
  metadata: SkiAreaMetadata;
  ski_area: {
    timestamps: string[];
    source?: string[];
    values: {
      base_temperature: Array<number | null>;
      summit_temperature: Array<number | null>;
      freezing_level: Array<number | null>;
      snow_level: Array<number | null>;
      snow_hourly_base: Array<number | null>;
      snow_hourly_summit: Array<number | null>;
      swe: Array<number | null>;
      wind_dir_summit: Array<number | null>;
      wind_gust_summit: Array<number | null>;
      wind_speed_summit: Array<number | null>;
      cloud_cover: Array<number | null>;
    };
  };
}

interface SkiAreaTableProps {
  data: SkiAreaDataStructure;
}

interface FlattenedSkiAreaRow {
  parameter: string;
  [key: string]: any;
}

export function SkiAreaTable({ data }: SkiAreaTableProps) {
  const [groupInterval, setGroupInterval] = useState<GroupInterval>('3');
  // Group data based on interval
  const groupedData = useMemo<SkiAreaDataStructure>(() => {
    if (groupInterval === '1') {
      return data;
    }

    const intervalHours = parseInt(groupInterval);
    const { timestamps } = groupByInterval(
      data.ski_area.timestamps,
      Object.values(data.ski_area.values)[0], // Use any value array to get grouped timestamps
      intervalHours
    );

    // Create new result with grouped data
    const result: SkiAreaDataStructure = {
      metadata: data.metadata,
      ski_area: {
        timestamps,
        source: data.ski_area.source,
        values: {}
      } as SkiAreaDataStructure['ski_area']
    };

    // Group each value array using the same timestamp groups and appropriate aggregation function
    Object.entries(data.ski_area.values).forEach(([key, values]) => {
      const { values: groupedValues } = groupByInterval(
        data.ski_area.timestamps, 
        values, 
        intervalHours,
        getAggregationFunction(key)
      );
      (result.ski_area.values as any)[key] = groupedValues;
    });

    return result;
  }, [data, groupInterval]);

  // Create timestamp columns
  const timestampColumns = useMemo(() => {
    if (!groupedData.ski_area.timestamps.length) return [];
    
    return groupedData.ski_area.timestamps.map(timestamp => {
      // Extract month and day directly from timestamp string
      const month = parseInt(timestamp.substring(5, 7)) - 1; // 0-based month
      const day = parseInt(timestamp.substring(8, 10));
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      return {
        field: timestamp,
        headerName: `${monthNames[month]} ${day}`,
        timestamp: timestamp,
        timestampMs: Date.parse(timestamp) // Only used for closest time comparison
      };
    });
  }, [groupedData.ski_area.timestamps]);

  // Find which time period the current time falls into
  const currentPeriodTimestamp = useMemo(() => 
    findCurrentPeriodTimestamp(timestampColumns, groupInterval)
  , [timestampColumns, groupInterval]);

  // Create time columns
  const timeColumns = useMemo<MRT_ColumnDef<FlattenedSkiAreaRow>[]>(() => {
    return timestampColumns.map((col): MRT_ColumnDef<FlattenedSkiAreaRow> => {
      const isCurrentPeriod = col.timestampMs === currentPeriodTimestamp;

      return {
        accessorKey: col.field,
        header: col.headerName,
        Header: () => <DayHeader timestamp={col.timestamp} groupInterval={groupInterval} />,
        enableColumnFilter: false,
        Cell: ({ cell, row }: { cell: MRT_Cell<FlattenedSkiAreaRow>; row: any }) => {
          const parameterLower = row.original.parameter.toLowerCase();
          const value = cell.getValue<number>();

          if (parameterLower.includes('snow summit') || parameterLower.includes('snow base')) {
            return <SnowRateCell value={value} groupInterval={groupInterval === '1' ? undefined : groupInterval} />;
          }
          
          if (parameterLower === 'wind direction (°)' || parameterLower === 'wind direction (summit)') {
            // Find the wind speed for this timestamp
            const timestamp = col.field;
            const windSpeedRow = rows.find(r => r.parameter === 'Summit Wind (mph)');
            const windSpeed = windSpeedRow ? windSpeedRow[timestamp] : null;
            return <WindDirectionCell value={value} windSpeed={windSpeed} />;
          }

          const isSnowLevel = parameterLower === 'snow level (ft)';
          const isCloudCover = parameterLower === 'cloud cover (%)';
          return (
            <DataCell
              value={value}
              isTemp={row.original.parameter.toLowerCase().includes('temp')}
              isPrecip={!isSnowLevel && (row.original.parameter.toLowerCase().includes('precip') || row.original.parameter.toLowerCase().includes('snow') || row.original.parameter.toLowerCase().includes('swe'))}
              isElevation={!isSnowLevel && row.original.parameter.toLowerCase().includes('level')}
              isCloudCover={isCloudCover}
              groupInterval={groupInterval}
              customFormat={isSnowLevel ? (value: number) => {
                const roundedValue = Math.round(value / 100) * 100;
                const displayValue = value > data.metadata.top_elevation ? 
                  Math.round(data.metadata.top_elevation / 100) * 100 : 
                  roundedValue;
                const inThousands = displayValue / 1000;
                const formattedValue = inThousands >= 1 ? `${inThousands.toFixed(1)}k` : `${displayValue}`;
                return value > data.metadata.top_elevation ? `>${formattedValue}` : formattedValue;
              } : undefined}
              customStyle={isSnowLevel ? () => getSnowLevelStyle(value, data.metadata.base_elevation, data.metadata.top_elevation) : undefined}
            />
          );
        },
        size: 44,
        enableColumnActions: false,
        muiTableHeadCellProps: {
          align: 'center',
          className: `timestamp-${col.timestampMs}`,
          sx: {
            ...timeColumnHeadStyle,
    borderLeft: isCurrentPeriod ? '1px solid red !important' : 'none !important',
    borderRight: isCurrentPeriod ? '1px solid red !important' : 'none !important',
    borderTop: isCurrentPeriod ? '1px solid red !important' : 'none !important',
    '&.MuiTableCell-head': {
      backgroundColor: '#ffffff !important'
            }
          },
        },
        muiTableBodyCellProps: ({ row }) => ({
          sx: {
            ...timeColumnBodyStyle,
            borderLeft: isCurrentPeriod ? '1px solid red !important' : 'none !important',
            borderRight: isCurrentPeriod ? '1px solid red !important' : 'none !important',
            borderBottom: isCurrentPeriod && row.index === rows.length - 1 ? '1px solid red !important' : 'none !important'
          },
        }),
      };
    });
  }, [timestampColumns, currentPeriodTimestamp]);

  // Create flattened rows
  const rows = useMemo(() => {
    const parameters = [
      { key: 'snow_hourly_summit', label: 'Snow Summit (in.)' },
      { key: 'snow_hourly_base', label: 'Snow Base (in.)' },
      { key: 'swe', label: 'SWE (in.)' },
      { key: 'snow_level', label: 'Snow Level (ft)' },
      { key: 'summit_temperature', label: 'Summit Temp (°F)' },
      { key: 'base_temperature', label: 'Base Temp (°F)' },
      { key: 'wind_dir_summit', label: 'Wind Direction (°)' },
      { key: 'wind_speed_summit', label: `Wind ${Math.round(groupedData.metadata.summit_wind_sensor_elevation)}' (mph)` },
      { key: 'wind_gust_summit', label: `Gust ${Math.round(groupedData.metadata.summit_wind_sensor_elevation)}' (mph)` },
      { key: 'cloud_cover', label: 'Cloud Cover (%)' }
    ];

    return parameters.map(param => {
      const row: FlattenedSkiAreaRow = {
        parameter: param.label
      };

      // Add data for each timestamp
      const values = groupedData.ski_area.values[param.key as keyof typeof groupedData.ski_area.values];
      if (Array.isArray(values)) {
        groupedData.ski_area.timestamps.forEach((timestamp, index) => {
          row[timestamp] = values[index];
        });
      }

      return row;
    });
  }, [groupedData]);

  // Create all columns
  const allColumns = useMemo<MRT_ColumnDef<FlattenedSkiAreaRow>[]>(() => {
    const baseColumns: MRT_ColumnDef<FlattenedSkiAreaRow>[] = [
      {
        accessorKey: 'parameter',
        header: 'Parameter',
        Header: () => <BaseHeader text="Parameter" />,
        enableColumnFilter: false,
        muiTableHeadCellProps: {
          sx: {
            ...stationNameHeadStyle,
            position: 'sticky',
            left: 0,
            zIndex: 11,
            backgroundColor: '#ffffff'
          }
        },
        muiTableBodyCellProps: {
          sx: {
            ...stationNameBodyStyle,
            position: 'sticky',
            left: 0,
            zIndex: 10,
            backgroundColor: '#ffffff'
          }
        }
      }
    ];

    return [...baseColumns, ...timeColumns];
  }, [timeColumns]);

  // Create refs to store table instance and container
  const tableInstanceRef = useRef<MRT_TableInstance<FlattenedSkiAreaRow>>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to current timestamp column when timestamp changes, group interval changes, or data changes (ski area changes)
  useEffect(() => {
    if (containerRef.current && currentPeriodTimestamp) {
      const currentColumnHeader = containerRef.current.querySelector(`.timestamp-${currentPeriodTimestamp}`);
      if (currentColumnHeader) {
        containerRef.current.scrollLeft = (currentColumnHeader as HTMLElement).offsetLeft - 100; // 100px offset for parameter column
      }
    }
  }, [currentPeriodTimestamp, groupInterval, data]);

  // Reset column order when timestampColumns changes
  useEffect(() => {
    if (tableInstanceRef.current) {
      tableInstanceRef.current.resetColumnOrder(true);
    }
  }, [timestampColumns]);

  // Function to export table data as CSV
  const exportToCsv = () => {
    // Create header row with Timestamp and all parameters
    const headers = ['Timestamp', ...rows.map(row => row.parameter)];

    // Create a row for each timestamp with all parameter values
    const csvRows = [
      headers.join(','),
      ...groupedData.ski_area.timestamps.map(timestamp => {
        const values = [
          timestamp, // Raw timestamp
          ...rows.map(row => {
            const value = row[timestamp];
            return value !== null ? value : '';
          })
        ];
        return values.join(',');
      })
    ];

    // Create and download CSV file
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ski-area-data-${groupInterval}hr.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div className="metadata-section" style={{ marginBottom: '8px' }}>
        <h3 style={{ display: 'inline-block', marginRight: '10px', verticalAlign: 'text-bottom' }}>Hourly Data</h3>
      </div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
        <FormControl component="fieldset">
          <ButtonGroup>
            <Button
              onClick={() => setGroupInterval('1')}
              variant={groupInterval === '1' ? 'contained' : 'outlined'}
              sx={{ fontSize: '11px', minWidth: '40px' }}
            >
              1hr
            </Button>
            <Button
              onClick={() => setGroupInterval('3')}
              variant={groupInterval === '3' ? 'contained' : 'outlined'}
              sx={{ fontSize: '11px', minWidth: '40px' }}
            >
              3hr
            </Button>
            <Button
              onClick={() => setGroupInterval('6')}
              variant={groupInterval === '6' ? 'contained' : 'outlined'}
              sx={{ fontSize: '11px', minWidth: '40px' }}
            >
              6hr
            </Button>
            <Button
              onClick={() => setGroupInterval('12')}
              variant={groupInterval === '12' ? 'contained' : 'outlined'}
              sx={{ fontSize: '11px', minWidth: '40px' }}
            >
              12hr
            </Button>
            <Button
              onClick={() => setGroupInterval('24')}
              variant={groupInterval === '24' ? 'contained' : 'outlined'}
              sx={{ fontSize: '11px', minWidth: '40px' }}
            >
              24hr
            </Button>
          </ButtonGroup>
        </FormControl>
        <Tooltip title="Export to CSV">
          <IconButton 
            onClick={exportToCsv}
            size="small"
            sx={{ verticalAlign: 'middle' }}
          >
            <FileDownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
      <DraggableContainer containerRef={containerRef}>
        <SkiAreaSensorTable 
          sensorRows={rows}
          columns={allColumns}
          onTableInstanceChange={(instance) => {
            tableInstanceRef.current = instance;
          }}
        />
      </DraggableContainer>
    </div>
  );
}
